import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ArrowBack, CheckCircle, Phone, VpnKey } from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: "#0f1117",
    color: "#fff",
    borderRadius: 16,
  },
  infoBox: {
    backgroundColor: "rgba(34, 158, 217, 0.08)",
    border: "1px solid rgba(34, 158, 217, 0.25)",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  successBox: {
    backgroundColor: "rgba(76, 175, 80, 0.08)",
    border: "1px solid rgba(76, 175, 80, 0.3)",
    borderRadius: 8,
    padding: theme.spacing(2),
    textAlign: "center",
  },
  bodyText: {
    color: "rgba(255,255,255,0.85)",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      backgroundColor: "rgba(255,255,255,0.05)",
      "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
      "&:hover fieldset": { borderColor: "rgba(34, 158, 217, 0.5)" },
      "&.Mui-focused fieldset": { borderColor: "#229ED9" },
    },
    "& .MuiOutlinedInput-input": {
      color: "#fff",
    },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.65)" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#229ED9" },
    "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.55)" },
  },
}));

function TelegramPersonalModal({ open, onClose, onBack, onCompleted }) {
  const classes = useStyles();
  const [phase, setPhase] = useState("form");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [errorText, setErrorText] = useState("");
  const [serverReady, setServerReady] = useState(null);

  const reset = useCallback(() => {
    setPhase("form");
    setPhone("");
    setName("");
    setCode("");
    setPassword("");
    setErrorText("");
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    api
      .get("/hub/telegram-native/status")
      .then(({ data }) => setServerReady(data.configured === true))
      .catch(() => setServerReady(false));
  }, [open, reset]);

  const handleSendCode = async () => {
    if (!phone.trim()) {
      toast.warning(i18n.t("telegramPersonal.phoneRequired"));
      return;
    }
    setPhase("sending");
    setErrorText("");
    try {
      await api.post("/hub/telegram-native/send-code", {
        phone: phone.trim(),
        name: name.trim() || undefined,
      });
      setPhase("code");
      toast.success(i18n.t("telegramPersonal.codeSent"));
    } catch (e) {
      const msg = e.response?.data?.error || i18n.t("telegramPersonal.errors.sendCode");
      setErrorText(msg);
      setPhase("form");
      toast.error(msg);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim()) {
      toast.warning(i18n.t("telegramPersonal.codeRequired"));
      return;
    }
    setPhase("verifying");
    setErrorText("");
    try {
      const { data } = await api.post("/hub/telegram-native/verify-code", {
        phone: phone.trim(),
        code: code.trim(),
      });
      if (data.needPassword) {
        setPhase("password");
        toast.info(i18n.t("telegramPersonal.need2fa"));
        return;
      }
      setPhase("success");
      toast.success(i18n.t("telegramPersonal.success"));
      if (onCompleted) onCompleted();
    } catch (e) {
      const msg = e.response?.data?.error || i18n.t("telegramPersonal.errors.verifyCode");
      setErrorText(msg);
      setPhase("code");
      toast.error(msg);
    }
  };

  const handleVerifyPassword = async () => {
    if (!password) {
      toast.warning(i18n.t("telegramPersonal.passwordRequired"));
      return;
    }
    setPhase("verifying");
    setErrorText("");
    try {
      await api.post("/hub/telegram-native/verify-password", {
        phone: phone.trim(),
        password,
      });
      setPhase("success");
      toast.success(i18n.t("telegramPersonal.success"));
      if (onCompleted) onCompleted();
    } catch (e) {
      const msg = e.response?.data?.error || i18n.t("telegramPersonal.errors.verifyPassword");
      setErrorText(msg);
      setPhase("password");
      toast.error(msg);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.paper }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center">
          {onBack && (
            <IconButton edge="start" onClick={onBack} style={{ color: "#fff", marginRight: 8 }}>
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h6" style={{ color: "#fff", fontWeight: 700 }}>
            {i18n.t("telegramPersonal.title")}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {serverReady === false && (
          <Box className={classes.infoBox} mb={2}>
            <Typography variant="body2" color="error">
              {i18n.t("telegramPersonal.serverNotConfigured")}
            </Typography>
          </Box>
        )}

        {phase === "success" ? (
          <Box className={classes.successBox}>
            <CheckCircle style={{ fontSize: 48, color: "#4caf50" }} />
            <Typography variant="h6" style={{ marginTop: 8, color: "#fff" }}>
              {i18n.t("telegramPersonal.success")}
            </Typography>
          </Box>
        ) : null}

        {(phase === "form" || phase === "sending") && (
          <>
            <Box className={classes.infoBox}>
              <Typography variant="body2" className={classes.bodyText}>
                {i18n.t("telegramPersonal.help")}
              </Typography>
            </Box>
            <TextField
              className={classes.input}
              fullWidth
              variant="outlined"
              label={i18n.t("telegramPersonal.phoneLabel")}
              placeholder="+5518998020650"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              margin="normal"
              helperText={i18n.t("telegramPersonal.phoneHint")}
            />
            <TextField
              className={classes.input}
              fullWidth
              variant="outlined"
              label={i18n.t("telegramPersonal.nameLabel")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              margin="normal"
            />
            {errorText && (
              <Typography color="error" variant="body2">
                {errorText}
              </Typography>
            )}
          </>
        )}

        {(phase === "code" || phase === "verifying") && phase !== "password" && (
          <>
            <Typography variant="body2" className={classes.bodyText} gutterBottom>
              {i18n.t("telegramPersonal.codeHelp", { phone })}
            </Typography>
            <TextField
              className={classes.input}
              fullWidth
              variant="outlined"
              label={i18n.t("telegramPersonal.codeLabel")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              margin="normal"
              autoFocus
            />
            {errorText && (
              <Typography color="error" variant="body2">
                {errorText}
              </Typography>
            )}
          </>
        )}

        {phase === "password" && (
          <>
            <Typography variant="body2" className={classes.bodyText} gutterBottom>
              {i18n.t("telegramPersonal.passwordHelp")}
            </Typography>
            <TextField
              className={classes.input}
              fullWidth
              variant="outlined"
              type="password"
              label={i18n.t("telegramPersonal.passwordLabel")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              autoFocus
            />
            {errorText && (
              <Typography color="error" variant="body2">
                {errorText}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ color: "rgba(255,255,255,0.7)" }}>
          {phase === "success" ? i18n.t("telegramPersonal.close") : i18n.t("telegramPersonal.cancel")}
        </Button>
        {phase === "form" && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleSendCode}
            disabled={phase === "sending" || serverReady === false}
            startIcon={phase === "sending" ? <CircularProgress size={18} /> : <Phone />}
          >
            {i18n.t("telegramPersonal.sendCode")}
          </Button>
        )}
        {phase === "code" && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleVerifyCode}
            disabled={phase === "verifying"}
            startIcon={phase === "verifying" ? <CircularProgress size={18} /> : <VpnKey />}
          >
            {i18n.t("telegramPersonal.verifyCode")}
          </Button>
        )}
        {phase === "password" && (
          <Button
            color="primary"
            variant="contained"
            onClick={handleVerifyPassword}
            disabled={phase === "verifying"}
          >
            {i18n.t("telegramPersonal.verifyPassword")}
          </Button>
        )}
        {phase === "success" && (
          <Button color="primary" variant="contained" onClick={onClose}>
            {i18n.t("telegramPersonal.close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default TelegramPersonalModal;
